import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, Typography, TextField, Button,  
    CircularProgress, Checkbox, MenuItem,
    FormControlLabel, Select, FormControl, InputLabel,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const SupplierPage = () => {
    useAuthCheck();
    const { supplierId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); 

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [airports, setAirports] = useState([]);
    const [formValues, setFormValues] = useState({
        supplierCode: '',
        supplierName: '',
        contactPerson: '',
        email: '',       
        phone: '',
        address: '',
        city: '',
        country: '',
        airports: [],
        isActive: true,
    });
    
    
    const fetchAirports = useCallback(async () => {
        try {            
            const res = await api.get('/airports');        
            setAirports(res.data);           
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch airports!");
            setError(error);
        } 
    }, []); 

    const fetchSupplier = useCallback(async () => {
        try {
            if (supplierId) {
                const res = await api.get(`/suppliers/${supplierId}`);
            
                setFormValues({
                    supplierCode: res.data.supplierCode || '',
                    supplierName: res.data.supplierName || '',                    
                    contactPerson: res.data.contactPerson || '',
                    email: res.data.email || '',       
                    phone: res.data.phone || '',
                    address: res.data.address || '',
                    city: res.data.city || '',
                    country: res.data.country || '',
                    airports: res.data.airports || [],
                    isActive: res.data.isActive !== undefined ? res.data.isActive : true,
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch supplier!");
            setError(error);
        } 
    }, [supplierId]); 

    useEffect(() => {     
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchAirports(); 
                await fetchSupplier();
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData(); 
    }, [fetchAirports, fetchSupplier]); 
    
    const checkoutSchema = yup.object().shape({
        supplierName: yup.string().required('Required'),       
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = supplierId ? "PUT" : "POST";
            const url = supplierId
                ? `/suppliers/update/${supplierId}`
                : '/suppliers/new';

            await api({
                method,
                url,
                data: {
                    supplierCode: values.supplierCode,
                    supplierName: values.supplierName,                                   
                    contactPerson: values.contactPerson,
                    email: values.email,       
                    phone: values.phone,
                    address: values.address,
                    city: values.city,
                    country: values.country,
                    airports: values.airports,
                    isActive: values.isActive,
                },
            });

            toast.success(supplierId 
                ? "Supplier updated successfully." 
                : "Supplier created successfully.");

            // onSubmitProps.resetForm();
            navigate("/suppliers");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update supplier!");
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Supplier"
                    subtitle={`${loading ? 'Loading...' : supplierId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/suppliers")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Supplier Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.supplierCode}
                                        name="supplierCode"
                                        error={!!touched.supplierCode && !!errors.supplierCode}
                                        helperText={touched.supplierCode && errors.supplierCode}
                                        sx={{ gridColumn: 'span 6' }}
                                    />
                                    <TextField
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Supplier Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.supplierName}
                                        name="supplierName"
                                        error={!!touched.supplierName && !!errors.supplierName}
                                        helperText={touched.supplierName && errors.supplierName}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Person"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactPerson}
                                        name="contactPerson"
                                        error={!!touched.contactPerson && !!errors.contactPerson}
                                        helperText={touched.contactPerson && errors.contactPerson}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Email"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.email}
                                        name="email"
                                        error={!!touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Phone"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.phone}
                                        name="phone"
                                        error={!!touched.phone && !!errors.phone}
                                        helperText={touched.phone && errors.phone}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Address"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.address}
                                        name="address"
                                        error={!!touched.address && !!errors.address}
                                        helperText={touched.address && errors.address}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="City"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.city}
                                        name="city"
                                        error={!!touched.city && !!errors.city}
                                        helperText={touched.city && errors.city}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Country"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.country}
                                        name="country"
                                        error={!!touched.country && !!errors.country}
                                        helperText={touched.country && errors.country}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControl variant="filled" sx={{ gridColumn: "span 6" }} >
                                        <InputLabel>Airports</InputLabel>
                                        <Select
                                            multiple
                                            label="Airports"
                                            name="airports"                                            
                                            value={values.airports || []}
                                            onChange={(e) => setFieldValue("airports", e.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {airports.map((airport, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={typeof airport === 'object' ? airport.iataCode : airport.airportName}
                                                >
                                                    {airport.iataCode} - {airport.airportName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {supplierId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default SupplierPage;
